import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { ICompanies } from './types'
import { Companies } from './ui'

import { useCompanies } from '@api/companies/state'

import { useValue } from '@hooks/useValue'
import { useDelayedInput } from '@hooks/useDelayedInput'

import { IModalPreview, ModalPreview } from '@primitive/modal.preview'
import { CardModeType } from '@smart/common.type'
import { CardCompanyContainer, ICardCompanyContainer } from '@smart/cardCompany'

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


/** Контрагенты - Контейнер */
export const CompaniesContainer = () => {
    const notify = useContext(NotificationContext)

    const isAccessDeniedMessage = (message?: string) => {
        const normalizedMessage = (message || '').toLowerCase()

        return normalizedMessage.includes('доступ') || normalizedMessage.includes('допуск')
    }

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    const companiesState = useCompanies(useShallow((state) => state))
    
    const cardCompanies = useValue<ICardCompanyContainer>({isOpen: false})
    const modalPreview = useValue<IModalPreview>({isOpen: false})

    const currentPageNumber = useValue<number>(1)

    //=======================================================================

    useEffect(() => {
        return (() => {
            companiesState.clearCompaniesState()

            currentPageNumber.setValue(1)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if (!companiesState.error) return
        if (isAccessDenied.value || isAccessDeniedMessage(companiesState.error.message)) return

        return notify && notifyOpen(companiesState.error.message, companiesState.error.type, 3000, notify)
    }, [companiesState.error, isAccessDenied.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        companiesState.isReloadCompanies && loadPrimaryData(txtSearch)
    }, [companiesState.isReloadCompanies]) // eslint-disable-line react-hooks/exhaustive-deps

    //=======================================================================

    /** Функция по получению списка слушателей */
    const loadPrimaryData = async (search: string) => {
        // companiesState.actionGetCompanies(currentPageNumber.value, search || '')
        companiesState.actionGetCompanies(currentPageNumber.value, search).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    companiesState.clearCompaniesState()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })

    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 500, (val) => loadPrimaryData(val))

    //=======================================================================

    /** Открытие/закрытие карточки по контрагенту */
    const handleOpenCardCompany = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardCompanies.setValue({
            isOpen, cardMode, cardID,
            handlerButtonCancel() {cardCompanies.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardCompanies.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    /** Открытие/закрытие окна предпросмотра документа */
    const handleOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }

    /** Функция по скачиванию файлов контрагента */
    const handleDownloadFiles = (filePath: string, fileName: string) => {
        const link = document.createElement('a') // Создание ссылки и программное скачивание файла

        link.href = filePath
        link.download = fileName
        document.body.appendChild(link)

        link.click()
        document.body.removeChild(link)
    }

    //=======================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICompanies = {
        isAccessDenied,
        isLoading: companiesState.companies.isLoading,
        txtSearch,

        currentPageNumber, 
        totalCount: companiesState.companies.totalCount, 
        pageSize: companiesState.companies.pageSize,

        companiesList: companiesState.companies.items,
        
        setTxtSearch, loadPrimaryData, 
        handleOpenCardCompany, handleOpenModalPreview, handleDownloadFiles
    }
    
    return <>
        <Companies {...propsToComponent} />
        <CardCompanyContainer {...cardCompanies.value} />
        
        <ModalPreview {...modalPreview.value} />
    </>
}